import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Zap, Siren, BarChart3, Shield, Globe2, ScanLine, Monitor, CheckCircle2 } from "lucide-react";
import { Brand, LanguageToggle } from "../components/Brand";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

export default function Landing() {
  const { t } = useI18n();
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get("/public/tenant/clinique-sante-plus").then(({ data }) => setTenant(data.tenant)).catch(() => {});
  }, []);

  const slug = tenant?.slug || "clinique-sante-plus";

  const features = [
    { icon: ScanLine, title: t("feature_kiosk"), desc: t("feature_kiosk_desc"), tone: "bg-blue-50 text-blue-700 border-blue-100" },
    { icon: Zap, title: t("feature_realtime"), desc: t("feature_realtime_desc"), tone: "bg-violet-50 text-violet-700 border-violet-100" },
    { icon: Siren, title: t("feature_priority"), desc: t("feature_priority_desc"), tone: "bg-rose-50 text-rose-700 border-rose-100" },
    { icon: BarChart3, title: t("feature_dashboard"), desc: t("feature_dashboard_desc"), tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    { icon: Shield, title: t("feature_multitenant"), desc: t("feature_multitenant_desc"), tone: "bg-amber-50 text-amber-700 border-amber-100" },
    { icon: Globe2, title: t("feature_bilingual"), desc: t("feature_bilingual_desc"), tone: "bg-slate-50 text-slate-700 border-slate-200" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link
              to="/login"
              data-testid="cta-login"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 transition"
            >
              {t("login")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-800 text-xs font-medium border border-blue-100 mb-6">
              <Activity className="w-3.5 h-3.5" /> Queue Management SaaS
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
              {t("tagline")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("feature_kiosk_desc")} {t("feature_realtime_desc")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/login"
                data-testid="hero-get-started"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800 transition"
              >
                {t("get_started")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={`/kiosk/${slug}`}
                data-testid="hero-try-kiosk"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-900 font-medium hover:border-slate-400 transition"
              >
                <ScanLine className="w-4 h-4" /> {t("open_kiosk")}
              </Link>
              <Link
                to={`/display/${slug}`}
                data-testid="hero-try-display"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-900 font-medium hover:border-slate-400 transition"
              >
                <Monitor className="w-4 h-4" /> {t("open_display")}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Multi-tenant</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Temps réel</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Priorité Urgence</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> JWT sécurisé</div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <img
                src="https://images.unsplash.com/photo-1764727291644-5dcb0b1a0375?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Modern clinic reception"
                className="w-full h-full object-cover aspect-[4/3]"
              />
              <div className="absolute top-4 left-4 rounded-2xl bg-white/95 backdrop-blur px-4 py-3 border border-slate-200 shadow-sm">
                <div className="text-xs text-slate-500 uppercase tracking-widest">{t("now_serving")}</div>
                <div className="font-heading text-3xl font-bold text-blue-800">U-012 → Salle 2</div>
              </div>
              <div className="absolute bottom-4 right-4 rounded-2xl bg-blue-700 text-white px-4 py-3 shadow-lg">
                <div className="text-xs uppercase tracking-widest opacity-80">{t("avg_wait_time")}</div>
                <div className="font-heading text-2xl font-bold">8 min</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-sm uppercase tracking-[0.2em] text-blue-700 font-medium mb-3">{t("features_label")}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">{t("features_title")}</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div key={idx} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-slate-300 transition">
                  <div className={`w-11 h-11 grid place-items-center rounded-xl border ${f.tone}`}>
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </div>
                  <div className="mt-4 font-heading font-semibold text-lg text-slate-900">{f.title}</div>
                  <div className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            {t("demo_banner")}
          </h3>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            superadmin@filepro.com · admin@cliniquesanteplus.com · agent@cliniquesanteplus.com
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-700 text-white font-medium hover:bg-blue-800 transition"
              data-testid="cta-try-demo"
            >
              {t("try_demo")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between text-sm text-slate-500">
          <div>© {new Date().getFullYear()} FilePro</div>
          <div>Built for clinics • Multi-tenant SaaS</div>
        </div>
      </footer>
    </div>
  );
}
